import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    Typography,
    Divider,
} from '@sunwisesoftware/sunwise-ui';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';

import { Tooltip } from 'common/components';
import {
    ReactHookFormIntlNumberInput,
    ReactHookFormMultiSelect,
    ReactHookFormSelect,
} from 'common/components/form/bootstrap';
import { getPolygonHeadings } from 'common/components/segment/asceHelpers';
import { POLYGON_RECTANGLE } from 'common/components/segment/constants';
import LandCategoryWithTooltip from 'common/components/segment/LandCategoryWithTooltip';
import SloofTypeFieldWithTooltip from 'common/components/segment/SloofTypeFieldWithTooltip';
import {
    beamSpacingOptions,
    foilGaugeOptions,
    gClampOptions,
    getFieldsToRender,
    getStrategyOptions,
    lFootSupportsWidthOptions,
    plateSupportLengthOptions,
    railSpacingOptions,
    railSupportLengthOptions,
    supportLengthOptions,
} from 'common/utils/helpers/systemsValues';

import resource_gclamp from 'resources/gclamp.png';
import resource_hbd from 'resources/hbd.png';
import resource_larp from 'resources/larp.png';
import resource_lee from 'resources/lee.png';
import resource_re from 'resources/re.png';

const buildBuildingHeadingField = ({
    control,
    drawingType,
    google,
    isNew,
    polygon,
}) => {
    if (
        Number(drawingType) !== POLYGON_RECTANGLE ||
        !google ||
        isNew === true ||
        !polygon
    )
        return null;

    const polygonHeadings = getPolygonHeadings({
        google,
        polygonCoords: polygon,
    });

    const buildingHeadingOptions = polygonHeadings.map(({ heading }) => ({
        label: `${heading}°`,
        value: `${heading}`,
    }));

    const building_heading = (
        <ReactHookFormSelect
            control={control}
            label={i18next.t('Fall orientation')}
            name="building_heading"
            options={buildingHeadingOptions}
        />
    );

    return building_heading;
};

const fieldsBuilder = ({
    control,
    google,
    isboom,
    isNew,
    onChangeSloofType,
    systemUid,
}) => {
    const fieldsToRender = getFieldsToRender(systemUid);
    const { t } = useTranslation();
    const strategyOptions = getStrategyOptions();

    const [
        beam_spacing,
        drawing_mode,
        front_base_height,
        lfoot_supports_width,
        outer_rail_length,
        plate_support_length,
        polygon,
        rail_spacing,
        rail_support_length,
        support_length,
    ] = useWatch({
        control,
        name: [
            'beam_spacing',
            'drawing_mode',
            'front_base_height',
            'lfoot_supports_width',
            'outer_rail_length',
            'plate_support_length',
            'polygon',
            'rail_spacing',
            'rail_support_length',
            'support_length',
        ],
    });

    const sloof_type = fieldsToRender.includes('sloof_type') &&
        isNew === true && (
            <SloofTypeFieldWithTooltip
                control={control}
                disabled={
                    Number(drawing_mode) !== POLYGON_RECTANGLE &&
                    isboom === false
                }
                onChange={onChangeSloofType}
                systemUid={systemUid}
            />
        );

    const building_heading =
        fieldsToRender.includes('sloof_type') &&
        buildBuildingHeadingField({
            control,
            drawingType: drawing_mode,
            google,
            isNew,
            polygon,
        });

    const ceil_tilt = fieldsToRender.includes('ceil_tilt') &&
        isNew === true && (
            <ReactHookFormIntlNumberInput
                append="°"
                control={control}
                fullWidth
                label={i18next.t('Ceil tilt')}
                name="ceil_tilt"
            />
        );

    const seam_distance = fieldsToRender.includes('seam_distance') && (
        <ReactHookFormIntlNumberInput
            append="mm"
            control={control}
            fullWidth
            label={i18next.t('Seam distance')}
            name="seam_distance"
        />
    );

    const sheet_gauge = fieldsToRender.includes('sheet_gauge') && (
        <ReactHookFormSelect
            control={control}
            label={i18next.t('Sheet gauge')}
            name="sheet_gauge"
            options={foilGaugeOptions}
        />
    );

    const gclamp_type = fieldsToRender.includes('gclamp_type') && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <ReactHookFormSelect
                    control={control}
                    label={i18next.t('Gclamp')}
                    name="gclamp_type"
                    options={gClampOptions}
                />
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_gclamp} />
                    <Trans>
                        To find out more, consult the following{' '}
                        <a
                            href="https://drive.google.com/file/d/1juZo9g-dYBtslj9IitJpFU7kyD1G01bs/view"
                            target="_blank"
                            rel="noreferrer"
                        >
                            link
                        </a>
                    </Trans>
                </Tooltip>
            </Box>
        </Box>
    );

    const spacing = isNew ? 2 : 0;
    const size = isNew ? 'grow' : 18;

    const rail_spacing_field = fieldsToRender.includes('rail_spacing') && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={spacing}>
                    <Grid size={size}>
                        <ReactHookFormSelect
                            control={control}
                            label={i18next.t('Rail length')}
                            name="rail_spacing.strategy"
                            options={[
                                ...strategyOptions,
                                { label: i18next.t('Custom'), value: 'custom' },
                            ]}
                        />
                    </Grid>

                    {rail_spacing?.strategy === 'manual' && (
                        <Grid size={size}>
                            <ReactHookFormMultiSelect
                                allTextValue={t('All text', { count: 2 })}
                                control={control}
                                label={i18next.t('Rail length')}
                                name="rail_spacing.value"
                                options={railSpacingOptions() || []}
                            />
                        </Grid>
                    )}

                    {rail_spacing?.strategy === 'custom' && (
                        <Grid size={size}>
                            <ReactHookFormIntlNumberInput
                                append="mm"
                                control={control}
                                label={i18next.t('Rail length')}
                                name="rail_spacing.custom_value"
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_re} />
                </Tooltip>
            </Box>
        </Box>
    );

    const rail_support_length_field = fieldsToRender.includes(
        'rail_support_length',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={spacing}>
                    <Grid size={size}>
                        <ReactHookFormSelect
                            control={control}
                            label={i18next.t('Rail support length')}
                            name="rail_support_length.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {rail_support_length?.strategy === 'manual' && (
                        <Grid size={size}>
                            <ReactHookFormSelect
                                control={control}
                                label={i18next.t('Rail support length')}
                                name="rail_support_length.value"
                                options={railSupportLengthOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_larp} />
                </Tooltip>
            </Box>
        </Box>
    );

    const plate_support_length_field = fieldsToRender.includes(
        'plate_support_length',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={spacing}>
                    <Grid size={size}>
                        <ReactHookFormSelect
                            control={control}
                            label={i18next.t('Plate support length')}
                            name="plate_support_length.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {plate_support_length?.strategy === 'manual' && (
                        <Grid size={size}>
                            <ReactHookFormSelect
                                control={control}
                                label={i18next.t('Plate support length')}
                                name="plate_support_length.value"
                                options={plateSupportLengthOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_larp} />
                </Tooltip>
            </Box>
        </Box>
    );

    const beam_spacing_field = fieldsToRender.includes('beam_spacing') && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={spacing}>
                    <Grid size={size}>
                        <ReactHookFormSelect
                            control={control}
                            label={i18next.t('Beam spacing')}
                            name="beam_spacing.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {beam_spacing?.strategy === 'manual' && (
                        <Grid size={size}>
                            <ReactHookFormSelect
                                control={control}
                                label={i18next.t('Beam spacing')}
                                name="beam_spacing.value"
                                options={beamSpacingOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_lee} />
                </Tooltip>
            </Box>
        </Box>
    );

    const support_length_field = fieldsToRender.includes('support_length') && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={spacing}>
                    <Grid size={size}>
                        <ReactHookFormSelect
                            control={control}
                            label={
                                systemUid === 'ARCK-LOSA CONVENCIONAL'
                                    ? t('Column spacing')
                                    : i18next.t('Support length')
                            }
                            name="support_length.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {support_length?.strategy === 'manual' && (
                        <Grid size={size}>
                            <ReactHookFormSelect
                                control={control}
                                label={
                                    systemUid === 'ARCK-LOSA CONVENCIONAL'
                                        ? t('Column spacing')
                                        : i18next.t('Support length')
                                }
                                name="support_length.value"
                                options={supportLengthOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_lee} />
                </Tooltip>
            </Box>
        </Box>
    );

    const lfoot_supports_width_field = fieldsToRender.includes(
        'lfoot_supports_width',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={spacing}>
                    <Grid size={size}>
                        <ReactHookFormSelect
                            control={control}
                            label={i18next.t('Lfoot supports width')}
                            name="lfoot_supports_width.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {lfoot_supports_width?.strategy === 'manual' && (
                        <Grid size={size}>
                            <ReactHookFormSelect
                                control={control}
                                label={i18next.t('Lfoot supports width')}
                                name="lfoot_supports_width.value"
                                options={lFootSupportsWidthOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_larp} />
                </Tooltip>
            </Box>
        </Box>
    );

    const outer_rail_length_field = fieldsToRender.includes(
        'outer_rail_length',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={spacing}>
                    <Grid size={size}>
                        <ReactHookFormSelect
                            control={control}
                            label={i18next.t('Outer rail length')}
                            name="outer_rail_length.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {outer_rail_length?.strategy === 'manual' && (
                        <Grid size={size}>
                            <ReactHookFormIntlNumberInput
                                append="mm"
                                control={control}
                                fullWidth
                                label={i18next.t('Outer rail length')}
                                name="outer_rail_length.value"
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_re} />
                </Tooltip>
            </Box>
        </Box>
    );

    const front_base_height_field = fieldsToRender.includes(
        'front_base_height',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={spacing}>
                    <Grid size={size}>
                        <ReactHookFormSelect
                            control={control}
                            label={i18next.t('Front base height')}
                            name="front_base_height.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {front_base_height?.strategy === 'manual' && (
                        <Grid size={size}>
                            <ReactHookFormIntlNumberInput
                                append="mm"
                                control={control}
                                fullWidth
                                label={i18next.t('Front base height')}
                                name="front_base_height.value"
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_hbd} />
                </Tooltip>
            </Box>
        </Box>
    );

    const showsAnyAdditionalSettings =
        rail_spacing_field ||
        rail_support_length_field ||
        plate_support_length_field ||
        beam_spacing_field ||
        support_length_field ||
        lfoot_supports_width_field ||
        outer_rail_length_field ||
        front_base_height_field;

    return (
        <>
            <>{sloof_type}</>
            <>{building_heading}</>
            <>{ceil_tilt}</>
            <>{seam_distance}</>
            <>{sheet_gauge}</>
            <>{gclamp_type}</>

            {showsAnyAdditionalSettings && (
                <>
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ m: 0, p: 0 }}
                        >
                            <Typography fontWeight="bold" variant="caption">
                                {t('Additional settings')}
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails sx={{ m: 0, p: 0 }}>
                            <>{rail_spacing_field}</>
                            <>{rail_support_length_field}</>
                            <>{plate_support_length_field}</>
                            <>{beam_spacing_field}</>
                            <>{support_length_field}</>
                            <>{lfoot_supports_width_field}</>
                            <>{outer_rail_length_field}</>
                            <>{front_base_height_field}</>
                        </AccordionDetails>
                    </Accordion>

                    <Divider sx={{ mb: 2 }} />
                </>
            )}
        </>
    );
};

const RenderInputsPerSystem = ({
    control,
    google,
    isboom = false,
    isNew = false,
    onChangeSloofType,
    systemUid,
}) => {
    return (
        <>
            <LandCategoryWithTooltip control={control} systemUid={systemUid} />

            {systemUid !== 'ARCK-GRANJA LOSA' && isNew === true && (
                <ReactHookFormIntlNumberInput
                    append="m"
                    control={control}
                    fullWidth
                    label={i18next.t('Building height')}
                    name="building_height"
                />
            )}

            {fieldsBuilder({
                control,
                isboom,
                isNew,
                google,
                onChangeSloofType,
                systemUid,
            })}
        </>
    );
};

RenderInputsPerSystem.propTypes = {
    control: PropTypes.object.isRequired,
    google: PropTypes.object,
    isboom: PropTypes.bool,
    isNew: PropTypes.bool,
    onChangeSloofType: PropTypes.func,
    systemUid: PropTypes.string.isRequired,
};

export default RenderInputsPerSystem;
